/**
 * Clinical Note API Service
 * Handles all API calls for Progress Notes and Clinical Documentation
 */

import apiClient from './apiClient';
import {
  ClinicalNoteDTO,
  CreateClinicalNoteRequest,
  ClinicalNoteType,
  PageResponse,
} from '../types/clinicalNote.types';

const BASE_PATH = '/v1/clinical-notes';

class ClinicalNoteService {
  /**
   * Get all clinical notes (paginated)
   */
  async getAllClinicalNotes(page: number = 0, size: number = 20): Promise<PageResponse<ClinicalNoteDTO>> {
    const response = await apiClient.get<PageResponse<ClinicalNoteDTO>>(BASE_PATH, {
      params: { page, size },
    });
    return response.data;
  }

  /**
   * Get clinical notes by resident
   */
  async getClinicalNotesByResident(residentId: string): Promise<ClinicalNoteDTO[]> {
    const response = await apiClient.get<ClinicalNoteDTO[]>(`${BASE_PATH}/resident/${residentId}`);
    return response.data;
  }

  /**
   * Get clinical notes by type
   */
  async getClinicalNotesByType(type: ClinicalNoteType): Promise<ClinicalNoteDTO[]> {
    const response = await apiClient.get<ClinicalNoteDTO[]>(`${BASE_PATH}/type/${type}`);
    return response.data;
  }

  /**
   * Get clinical note by ID
   */
  async getClinicalNoteById(id: string): Promise<ClinicalNoteDTO> {
    const response = await apiClient.get<ClinicalNoteDTO>(`${BASE_PATH}/${id}`);
    return response.data;
  }

  /**
   * Create a new clinical note
   */
  async createClinicalNote(request: CreateClinicalNoteRequest): Promise<ClinicalNoteDTO> {
    const response = await apiClient.post<ClinicalNoteDTO>(BASE_PATH, request);
    return response.data;
  }

  /**
   * Update clinical note
   */
  async updateClinicalNote(id: string, data: Partial<ClinicalNoteDTO>): Promise<ClinicalNoteDTO> {
    const response = await apiClient.patch<ClinicalNoteDTO>(`${BASE_PATH}/${id}`, data);
    return response.data;
  }

  /**
   * Sign clinical note
   */
  async signClinicalNote(id: string): Promise<ClinicalNoteDTO> {
    const response = await apiClient.post<ClinicalNoteDTO>(`${BASE_PATH}/${id}/sign`);
    return response.data;
  }

  /**
   * Delete clinical note
   */
  async deleteClinicalNote(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  }

  /**
   * Get progress notes by resident (with optional day filter)
   */
  async getProgressNotesByResident(residentId: string, days?: number): Promise<any[]> {
    const response = await apiClient.get<any[]>(`/v1/progress-notes/resident/${residentId}`, {
      params: days ? { days } : {}
    });
    return response.data;
  }

  /**
   * Get progress notes by facility (with optional day filter)
   */
  async getProgressNotesByFacility(facilityId: string, days?: number): Promise<any[]> {
    const response = await apiClient.get<any[]>(`/v1/progress-notes/facility/${facilityId}`, {
      params: days ? { days } : {}
    });
    return response.data;
  }

  /**
   * Delete a progress note
   */
  async deleteProgressNote(id: string): Promise<void> {
    await apiClient.delete(`/v1/progress-notes/${id}`);
  }

  /**
   * Create a new progress note
   */
  async createProgressNote(payload: any): Promise<any> {
    const response = await apiClient.post<any>('/v1/progress-notes', payload);
    return response.data;
  }

  /**
   * Update an existing progress note
   */
  async updateProgressNote(id: string, payload: any): Promise<any> {
    const response = await apiClient.put<any>(`/v1/progress-notes/${id}`, payload);
    return response.data;
  }

  /**
   * Get nurse notes for a resident
   */
  async getNurseNotesByResident(residentId: string): Promise<any[]> {
    const response = await apiClient.get<any[]>(`/v1/nurse-notes?residentId=${residentId}`);
    return response.data;
  }

  /**
   * Create a new nurse note (Daily Medication)
   */
  async createNurseNote(payload: any, signatureFile?: File | Blob): Promise<any> {
    const formData = new FormData();
    formData.append(
      'note',
      new Blob([JSON.stringify(payload)], { type: 'application/json' })
    );

    if (signatureFile) {
      const fileName = (signatureFile instanceof File && signatureFile.name && signatureFile.name.includes('.'))
        ? signatureFile.name
        : 'signature.png';
      formData.append('file', signatureFile, fileName);
    }

    const response = await apiClient.post<any>('/v1/nurse-notes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  /**
   * Update an existing nurse note
   */
  async updateNurseNote(id: string, payload: any, signatureFile?: File | Blob): Promise<any> {
    const formData = new FormData();
    formData.append(
      'note',
      new Blob([JSON.stringify(payload)], { type: 'application/json' })
    );

    if (signatureFile) {
      const fileName = (signatureFile instanceof File && signatureFile.name && signatureFile.name.includes('.'))
        ? signatureFile.name
        : 'signature.png';
      formData.append('signature', signatureFile, fileName);
      formData.append('file', signatureFile, fileName);
    }

    const response = await apiClient.put<any>(`/v1/nurse-notes/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  /**
   * Delete a nurse note
   */
  async deleteNurseNote(id: string): Promise<void> {
    await apiClient.delete(`/v1/nurse-notes/${id}`);
  }

  /**
   * Create bulk nurse notes
   */
  async createBulkNurseNotes(residentId: string, notes: any[]): Promise<any> {
    const response = await apiClient.post<any>(`/v1/residents/${residentId}/nurse-notes/bulk`, notes);
    return response.data;
  }
}

export default new ClinicalNoteService();
